import type { Article } from '../types';
import { h2, p, ul, callout, table } from '../b';

const article: Article = {
  slug: 'telegram-stars-price-usd-ton',
  title: 'How Much Is a Telegram Star Worth? Price in USD, EUR and TON',
  metaTitle: 'Telegram Stars Price: 1 Star in USD, EUR & TON (2026)',
  metaDescription:
    'How much does a Telegram Star cost and how much is it worth? Buyer prices in-app vs Fragment, seller conversion rate to TON, 100 / 500 / 1000 Stars in USD and EUR, and a simple calculator method.',
  keywords: [
    'telegram stars price',
    'how much is 1 telegram star',
    'telegram stars to usd',
    'telegram stars to ton',
    'telegram stars value',
    '100 telegram stars in dollars',
    'telegram stars calculator',
  ],
  category: 'Guides',
  datePublished: '2026-09-30',
  dateModified: '2026-10-12',
  readingTimeMin: 6,
  excerpt:
    'There is no single price for a Telegram Star. Buyers pay one rate that depends on where they buy, and sellers receive another when they convert to TON. This guide gives you both, with ready-made conversions for common amounts.',
  keyTakeaways: [
    'Buyers pay roughly $0.015 to $0.025 per Star in-app depending on the pack size and app store; Fragment (paid in TON) is usually cheaper.',
    'Sellers historically convert Stars to TON at a rate equivalent to about $0.013 per Star, so 100 Stars ≈ $1.30 and 1,000 Stars ≈ $13.',
    'The TON amount you receive fluctuates with the TON/USD price; the dollar value is more stable than the TON amount.',
    'Always check fragment.com/stars for the live rate before setting prices; Telegram can adjust it.',
  ],
  body: [
    h2('Two different prices for one Star'),
    p('When people ask "how much is a Telegram Star", they usually mean one of two things:'),
    ul([
      '**Buyer price**: what it costs to purchase Stars. Set by Telegram and the app stores, varies by pack and country.',
      '**Seller value**: what a Star is worth when a developer or creator converts it to TON. Set by Telegram\'s conversion rate on Fragment.',
    ]),
    p('The gap between the two covers app store commissions and Telegram\'s share.'),

    h2('Buyer price: what Stars cost'),
    p(
      'Stars are sold in packs from about 50 up to tens of thousands. The larger the pack, the lower the price per Star. Indicatively, in-app purchases land between **$0.015 and $0.025 per Star**, so a 100-Star pack costs around $2 and a 500-Star pack around $10. On [Fragment](https://fragment.com/stars), paid in TON, the price per Star is usually noticeably lower because no store commission applies. Prices in EUR are typically localised at a similar level.',
    ),

    h2('Seller value: what Stars are worth when you cash out'),
    p(
      'When a bot, channel or merchant withdraws Stars, Fragment converts them to TON. Telegram has pegged this conversion so that **1 Star ≈ $0.013** in TON terms, recalculated against the live TON price. If TON is at $5, 1,000 Stars convert to about 2.6 TON; if TON is at $2.50, the same Stars convert to about 5.2 TON. In dollar terms the amount is roughly constant.',
    ),
    callout('Rates can change. Treat $0.013 as a planning figure and verify on Fragment before publishing long-term prices.'),

    h2('Quick conversion table (seller side)'),
    table(
      ['Stars', 'Approx. USD value', 'Approx. EUR value', 'After 3% platform fee (USD)'],
      [
        ['50', '$0.65', '€0.60', '$0.63'],
        ['100', '$1.30', '€1.20', '$1.26'],
        ['250', '$3.25', '€3.00', '$3.15'],
        ['500', '$6.50', '€6.00', '$6.31'],
        ['1,000', '$13.00', '€12.00', '$12.61'],
        ['5,000', '$65.00', '€60.00', '$63.05'],
        ['10,000', '$130.00', '€120.00', '$126.10'],
      ],
    ),
    p('EUR figures assume roughly 1 USD ≈ 0.92 EUR and are rounded. The last column shows what you keep when using Telegram Stars Payment Platform, which charges a flat 3%.'),

    h2('How to calculate Stars to USD yourself'),
    ul([
      '**Seller**: `Stars × 0.013 = USD`. Then `USD ÷ TON price = TON received`.',
      '**Buyer**: look at the pack price in your store and divide by the number of Stars.',
      '**Pricing a product**: `desired USD ÷ 0.013 ÷ 0.97 = Stars to charge` (the 0.97 accounts for a 3% platform fee). Round up to a pack-friendly number.',
    ]),

    h2('Why the Stars price is higher in the App Store than on Fragment'),
    p(
      'Apple and Google take a commission on in-app purchases, and Telegram prices Stars so that it still receives a consistent amount after that commission. On Fragment, Stars are bought with TON outside the stores, so the commission disappears and the price drops. Telegram openly recommends Fragment to users who want the best rate.',
    ),

    h2('Does the Stars value fluctuate?'),
    p(
      'The dollar-equivalent conversion has been stable, but the TON amount you receive moves with the market every minute. If you want predictable revenue, convert TON to a stablecoin or fiat soon after withdrawal. See [How to withdraw Telegram Stars to TON](/blog/withdraw-telegram-stars-to-ton) for the full process.',
    ),

    h2('Stars value for buyers who want a refund'),
    p(
      'Users cannot convert Stars back to money. If a purchase goes wrong, the seller can refund the Stars (not the dollars) using `refundStarPayment`, and the user can spend them elsewhere in Telegram. Buyers should therefore purchase Stars in the amounts they intend to spend.',
    ),

    h2('Related reading'),
    ul([
      '[Telegram Stars fees explained](/blog/telegram-stars-fees-explained): where every cent goes.',
      '[What are Telegram Stars?](/blog/what-are-telegram-stars): the complete introduction.',
      '[Accept Telegram Stars payments](/blog/how-to-accept-telegram-stars-payments): start earning Stars today.',
    ]),
  ],
  faq: [
    {
      q: 'How much is 1 Telegram Star in USD?',
      a: 'About $0.013 for sellers converting to TON. Buyers pay roughly $0.015–$0.025 per Star in-app depending on the pack.',
    },
    {
      q: 'How much is 100 Telegram Stars?',
      a: 'Buying 100 Stars costs around $2 in-app. For a seller, 100 Stars are worth about $1.30 in TON at conversion.',
    },
    {
      q: 'How much is 1000 Telegram Stars in dollars?',
      a: 'Approximately $13 when converted to TON by a seller. Buying 1,000 Stars in-app costs roughly $15–$20 depending on the store.',
    },
    {
      q: 'Where is it cheapest to buy Telegram Stars?',
      a: 'On Fragment (fragment.com/stars), paid in TON, because no app store commission applies.',
    },
    {
      q: 'Is the Telegram Stars rate fixed?',
      a: 'The dollar-equivalent rate has been stable but is set by Telegram and can change. The TON amount varies with the TON price.',
    },
  ],
  relatedSlugs: [
    'telegram-stars-fees-explained',
    'withdraw-telegram-stars-to-ton',
    'what-are-telegram-stars',
  ],
};

export default article;
